import { useState } from 'react';
import { useGetIntakeFormsQuery } from '../../features/apis/aiApi';
import { FiCopy, FiExternalLink, FiRefreshCw } from 'react-icons/fi';
import { toast } from 'react-toastify';

export default function IntakeFormsTab() {
  const { data, isLoading, error, refetch } = useGetIntakeFormsQuery();
  const forms = data?.forms || [];

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Link copied to clipboard');
    } catch {
      toast.error('Failed to copy link');
    }
  };

  if (isLoading)
    return (
      <div className="flex justify-center items-center py-10">
        <div className="text-sm text-gray-500">Loading intake forms...</div>
      </div>
    );

  if (error)
    return (
      <div className="flex justify-between items-center py-4">
        <p className="text-sm text-red-500">Failed to load intake forms.</p>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-2 text-sm text-[#6E3EF5] border border-[#6E3EF5] px-3 py-1.5 rounded-md hover:bg-[#F3EFFE] transition-colors"
        >
          <FiRefreshCw size={14} />
          Retry
        </button>
      </div>
    );

  if (!forms.length)
    return (
      <div className="flex flex-col items-center justify-center py-10 gap-3">
        <p className="text-sm text-gray-500">No intake forms found.</p>
        <p className="text-xs text-gray-400">
          Ask the Recruiter Agent to generate an intake form for a job to get started.
        </p>
      </div>
    );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-[#161221]">
          Application Forms
        </h3>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-2 text-sm text-[#6E3EF5] border border-[#6E3EF5] px-3 py-1.5 rounded-md hover:bg-[#F3EFFE] transition-colors"
        >
          <FiRefreshCw size={14} />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {forms.map((form: any) => (
          <div
            key={form._id}
            className="rounded-xl border border-[#ECEAF5] bg-white p-4 shadow-sm"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-[#161221]">
                  {form.jobId?.jobTitle || 'Untitled Job'}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Status: <span className="capitalize">{form.isActive ? 'Active' : 'Inactive'}</span>
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Created: {new Date(form.createdAt).toLocaleDateString()}
                </p>
              </div>
            </div>

            {form.responderUri && (
              <div className="mt-3 flex items-center gap-2">
                <input
                  readOnly
                  value={form.responderUri}
                  className="flex-1 text-xs border border-gray-200 rounded-md px-3 py-2 bg-gray-50"
                />
                <button
                  onClick={() => copyToClipboard(form.responderUri)}
                  className="p-2 rounded-md border border-gray-200 hover:bg-gray-50 text-gray-600"
                  title="Copy link"
                >
                  <FiCopy size={16} />
                </button>
                <a
                  href={form.responderUri}
                  target="_blank"
                  rel="noreferrer"
                  className="p-2 rounded-md border border-gray-200 hover:bg-gray-50 text-gray-600"
                  title="Open form"
                >
                  <FiExternalLink size={16} />
                </a>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
